import { PRODUCT_TYPE_CODE } from '@repo/types'
import {
  QUANTITY_TIERS, FORM_CODE, sizeCode, visible2dOptionsFor, normalizeBuildOptions,
  type QuantityTier, type TagFormFactor,
} from './pricing'

/**
 * VRG-266 — the two-layer variant image model, and the asset naming contract that
 * photography drops into.
 *
 * ## Why layers
 *
 * A variant image is `package[quantity]` BEHIND `tag[appearance]`. The two axes are
 * independent, so storing them separately turns a PRODUCT into a SUM: shoot ~6 package
 * backgrounds and ~50-70 tag cutouts (≈60-76 files) instead of the ~300-420 flats their
 * cross-product would require. Adding a package size costs 1 image; redesigning a tag
 * costs 1 image. That saving is the entire reason this task exists.
 *
 * ## This module resolves; it does not render
 *
 * It maps a variant to the two asset names it needs. Deliberately pure and free of any
 * renderer, because there are TWO consumers with different capabilities:
 *
 *   - The shop UI composites the layers live (CSS stack) — no export step, so a catalog
 *     change is visible immediately.
 *   - A bake step composites the SAME pair into a flat PNG for the surfaces that cannot
 *     stack layers: `apps/vera-app`'s ScanPreview (React Native `<Image source={{uri}}>`
 *     — a web compositor does not port), transactional email (Outlook's CSS support makes
 *     overlays unreliable), and Shopify's own per-variant image slot.
 *
 * So flats still exist — they are GENERATED from this model rather than shot. The
 * combinatorial cost is removed from the photography, which is where it actually hurts,
 * not from the file count.
 *
 * `PackagePreview` is a platform React component and *could* composite, but it renders at
 * 28-48px where a two-layer stack is wasted detail; a generated flat is the cheaper call.
 */

/**
 * The dimensions that change what a tag LOOKS LIKE. Capability is deliberately absent:
 * it selects the chip inside, which is invisible in a photograph — and the chip family is
 * internal fulfilment detail we never surface to a buyer anyway.
 *
 * Every field beyond `form` and `size` is optional because it applies to only some forms
 * (durability to the fabrics, adhesive to Solid, cardMaterial to Card). An option that
 * does not apply is OMITTED from the key rather than encoded as a "none" token — see
 * `tagLayerAsset`.
 */
export interface TagAppearanceKey {
  form: TagFormFactor
  /** The physical size label as it appears in `FORM_META[form].sizes` (e.g. '30mm', '45x25mm'). */
  size: string
  /**
   * Build-option codes from the registry in pricing.ts — `['XD','EMBR','WHITE']`.
   *
   * ⚠️ ONE VOCABULARY, NOT A PARALLEL SET OF FLAGS. This used to be five typed
   * fields (`onMetal`, `durability`, `construction`, `adhesive`, `cardMaterial`)
   * restating the registry in a second shape. Every option added meant editing
   * both, and whichever was forgotten failed silently — the filename simply came
   * out wrong, which no type could catch.
   *
   * Codes the form cannot carry are rejected by `invalidBuildOption`; codes that
   * do not change a photograph are dropped here by the `visible2d` filter, so a
   * caller can pass the full build options of a SKU and get the right picture
   * without knowing which ones are visible.
   */
  options?: readonly string[]
}


/** The pair of assets a variant image is composited from. Either layer may be missing. */
export interface VariantLayers {
  /** Background — the box the tags ship in, by quantity tier. */
  packageSrc: string
  /** Foreground — the tag itself, transparent PNG. */
  tagSrc: string
}

/**
 * Where the imagery lives. A single root so the bake step and the UI cannot disagree.
 *
 * Assets live in **GCS**, not `apps/platform/public/` — buckets `vera-tag-imagery-dev` /
 * `vera-tag-imagery-prod`, following the `vera-certificates-app-{dev,prod}` convention and
 * served through the existing `gcloud-storage.driver`.
 *
 * The decisive reason is not repo hygiene, it is that `public/` yields a path relative to
 * the platform's own origin — and the two consumers the bake exists to serve, React
 * Native's `<Image source={{uri}}>` and an email client, both require an ABSOLUTE URL. A
 * relative path defeats the point of generating the flats at all. (Repo hygiene is a real
 * secondary: git keeps every version of every binary forever, so one tag redesign would
 * add another ~280 blobs permanently, and it would couple swapping a photo to shipping a
 * deploy.)
 *
 * Falls back to the relative path when unset, so a local checkout can drop files into
 * `public/tag-imagery/` and work with no bucket or credentials.
 */
export const TAG_IMAGERY_ROOT: string =
  (import.meta.env?.VITE_TAG_IMAGERY_BASE_URL as string | undefined)?.replace(/\/$/, '') ??
  '/tag-imagery'

/**
 * Quantity → package tier. Snaps to the LARGEST tier the quantity reaches, so an
 * off-catalog quantity (a bulk quote, a legacy SKU) still lands on a real box rather
 * than resolving to nothing.
 */
export const packageTierFor = (quantity: number): QuantityTier => {
  const ascending = [...QUANTITY_TIERS].sort((a, b) => a - b)
  let tier = ascending[0]
  for (const t of ascending) if (quantity >= t) tier = t
  return tier
}

/** Background asset for a quantity. ~4 of these today, 5-6 once more box sizes exist. */
export const packageLayerAsset = (quantity: number): string =>
  `${TAG_IMAGERY_ROOT}/package/package-${packageTierFor(quantity)}.png`

/**
 * Which face or treatment a picture shows.
 *
 * `front` is the default and is NEVER written, like every other default in this
 * scheme. The rest are semantic rather than numeric on purpose: Spencer's own
 * examples for the extra shots were "damages" and "different applications",
 * which are KINDS of picture, not positions in a list. `VERA-FPT-25-2` tells a
 * photographer nothing about what to shoot; `VERA-FPT-25-APPLIED` does.
 */
export type TagView = 'front' | 'back' | 'detail' | 'applied' | 'wear'

const VIEW_CODE: Record<Exclude<TagView, 'front'>, string> = {
  back: 'BACK', detail: 'DETAIL', applied: 'APPLIED', wear: 'WEAR',
}

/**
 * Foreground asset for a tag's appearance.
 *
 *   VERA-<FORM>-<SIZE>[-<OPTION>…][-<VIEW>][-<N>].png
 *
 * ⚠️ THE OPTION SEGMENTS COME FROM THE REGISTRY IN pricing.ts, FILTERED TO
 * `visible2d`. They are not restated here. That is the whole point: the same
 * list that builds a SKU and validates an order decides what changes a
 * photograph, so a new option cannot appear in one and be forgotten in the
 * other. On-metal and adhesive are excluded automatically because their specs
 * say `visible2d: false`, not because this function knows about them.
 *
 * ── The sequence suffix ───────────────────────────────────────────────────
 * Spencer, 2026-09-18: additional carousel images per SKU, "-1 -2 -3".
 *
 * Adopted with two changes:
 *
 *  1. THE PRIMARY STAYS BARE — there is no `-1`. Every default in this scheme
 *     is unwritten (black, Standard, Woven, Plastic, front), and making the
 *     primary the one exception would rename all 75 planned files for no gain.
 *     Extras therefore start at 2, which also matches Shopify: `images[]` is an
 *     ordered array whose first entry is the primary, so `-2` is literally the
 *     second image.
 *  2. THE SEQUENCE IS ALWAYS LAST, after the view. A second shot of the back is
 *     `…-BACK-2`, not `…-2-BACK`. A trailing integer is then unambiguous — size
 *     is always segment 3 and options are alphabetic — so a name parses back
 *     without a lookup table.
 *
 * ⚠️ EXTRAS ARE NOT IN THE GENERATED SHOT LIST, AND MUST NOT BE. The list is
 * the REQUIRED set — one per orderable appearance. Extras are discretionary and
 * unbounded; generating them would turn a finite checklist into an infinite one
 * and nobody could tell what "done" meant.
 */
export const tagLayerAsset = (
  key: TagAppearanceKey,
  opts: TagShotOptions = {},
): string => `${TAG_IMAGERY_ROOT}/tag/${tagLayerName(key, opts)}.png`

/**
 * What distinguishes one shot of the same appearance from another.
 *
 * `productType` is a PRODUCT TYPE NAME from `ALL_PRODUCT_TYPES` — "Wine", not
 * "WINE". The code is looked up, never passed in, so a caller cannot invent one
 * and a rename upstream cannot leave a stale code behind in call sites.
 */
export type TagShotOptions = {view?: TagView; productType?: string; seq?: number}

/** The bare filename, without root or extension. */
export const tagLayerName = (
  key: TagAppearanceKey,
  {view = 'front', productType, seq}: TagShotOptions = {},
): string => {
  const visible = new Set(visible2dOptionsFor(key.form).map((o) => o.code))
  const options = normalizeBuildOptions(key.form, key.options ?? [])
    .filter((c) => visible.has(c))
  if (seq !== undefined && (!Number.isInteger(seq) || seq < 2)) {
    /* Not a silent clamp: `seq: 1` almost always means "the primary", and
       emitting `…-1` would create a second name for a file that already exists
       bare — two records of one picture, which is the drift this file exists
       to avoid. */
    throw new Error(`tagLayerName: seq must be an integer >= 2 (got ${seq}); the primary image has no suffix`)
  }
  /* ⚠️ A NAME, RESOLVED TO A CODE — never a code passed straight through. An
     unknown product type is a THROW rather than a skipped segment: a silently
     untagged photo is invisible to the industry page that wanted it, and looks
     like missing photography rather than a typo. */
  let ptCode: string | undefined
  if (productType !== undefined) {
    ptCode = PRODUCT_TYPE_CODE[productType]
    if (!ptCode) {
      throw new Error(
        `tagLayerName: "${productType}" is not a product type. ` +
          `Use a value from ALL_PRODUCT_TYPES (e.g. "Wine", "Handbags").`,
      )
    }
  }
  return [
    'VERA', FORM_CODE[key.form], sizeCode(key.size), ...options,
    ...(view === 'front' ? [] : [VIEW_CODE[view]]),
    ...(ptCode === undefined ? [] : [ptCode]),
    ...(seq === undefined ? [] : [String(seq)]),
  ].join('-')
}

/**
 * VRG-266 — WHICH SIZE OF EACH FORM HAS ACTUALLY BEEN PHOTOGRAPHED.
 *
 * ⚠️ THIS IS A PROPERTY OF THE SHOT LIBRARY, NOT OF THE CATALOGUE, and that is
 * why it is here rather than in `catalog.ts`. A form offers up to seven sizes;
 * photography has delivered exactly one of them per form so far. Nothing in the
 * product model knows or should know that — a size stops being "the
 * photographed one" the day another render lands, with no catalogue change.
 *
 * ── What it is for ────────────────────────────────────────────────────────
 * `tagLayerAsset` resolves a name for ANY (form, size) and says so: "it cannot
 * know which files a photographer has actually delivered". That is right for a
 * renderer with a 404 fallback and wrong for a PRODUCT CARD, which needs one
 * representative picture and gets one shot at it. This map is the answer to
 * "which one", and it is deliberately incomplete — see below.
 *
 * ── ⚠️ Sticker is 20x20mm, and NOT the 28mm the filename would suggest ────
 * `VERA-STK-28.png` exists in the shot library and is a MISFILE: it is
 * byte-identical (md5 7c3ded53…) to `VERA-SLD-30.png`, in the supply repo and
 * in the marketing site's copy alike. The picture is the Solid tag — a thick
 * rigid puck with a deep shadowed rim, matching `VERA-SLD-30-BACK.png`'s
 * profile, and nothing like the genuine sticker back `VERA-STK-28-BACK.png`,
 * which is a thin wafer showing its antenna coil. Spencer confirmed on
 * 2026-09-22 that both SLD-30 faces are the same Solid tag.
 *
 * `VERA-STK-20x20.png` is a distinct render, 20x20mm is a size only Sticker
 * offers, and TAGS.md assigns it to Sticker. So it is the honest Sticker
 * primary, and the misfiled 28mm file is NOT staged at all — pointing two
 * products at one picture would publish a false claim about what a buyer
 * receives, which is the single thing this catalogue must not do.
 *
 * ── ⚠️ FabricLabel IS now photographed; ClosureLoop is still ABSENT ────────
 * This paragraph used to read "No render of either exists" for FabricLabel and
 * ClosureLoop alike. That was true when it was written and is now HALF WRONG,
 * which is the worse failure: a stale absence reads as a decision. The supply
 * library carries `VERA-FLB-65x25.png` and `VERA-FLB-70x25.png`, with matching
 * `-BACK` and `-WHITE` plates — four appearances, enough to model both sizes
 * (VRG-780). 65x25mm is the representative one: it is the smaller of the two
 * flat labels and the one whose whole option set is shot.
 *
 * The rule the paragraph stated still stands and is why FabricLabel waited: a
 * form is never pointed at a NEIGHBOUR's shot. The Fabric PATCH picture is not
 * a stand-in for the Fabric LABEL, because the entire claim a form makes is
 * that it is a different object. A consumer must handle `undefined` — that is
 * the contract, not an oversight.
 *
 * ClosureLoop's absence is the strongest case of the rule, not an exception to
 * it: the part IS NOT MANUFACTURED (VRG-630), so there is nothing to photograph
 * and the Closure shot would be a picture of a different object. It took this
 * slot from `Hang`, which was equally unphotographed for four months while
 * being sold.
 *
 * ── ⚠️ Closure is `S68`, a SUPPLY size token, not the catalogue's `SINGLE` ──
 * This read `'SINGLE'`, and `VERA-CLO-SINGLE.png` HAS NEVER EXISTED — the
 * Closure plates are `S68`, `S102`, `D110` and `D178`, so the Closure tile has
 * been resolving a broken image for as long as the entry has been here. The fix
 * is to name a plate that exists, and this map is the right place for it by its
 * own doctrine: it is a property of the SHOT LIBRARY, and the shot library
 * speaks the supply vocabulary.
 *
 * `SINGLE` means a single-arm Closure, which is what `S68` and `S102` both are,
 * so `S68` is a true picture of what a buyer selecting SINGLE receives — at a
 * length they cannot name in the shop. That the two vocabularies disagree at all
 * is a real defect and a separate ticket (six forms are affected; VRG-780's task
 * file has the table). It is recorded here rather than papered over.
 */
export const PHOTOGRAPHED_SIZE: Partial<Record<TagFormFactor, string>> = {
  Sticker: '20x20mm',
  Solid: '30mm',
  FabricPatch: '25x25mm',
  FabricLabel: '65x25mm',
  Closure: 'S68',
  Card: '85.6x54mm',
  Dial: '28mm',
}

/**
 * VRG-780 — forms whose photographed size is NOT a size the catalogue sells,
 * with the reason, because for these two forms the plate library and the shop
 * speak different vocabularies.
 *
 * ⚠️ THIS EXISTS BECAUSE THE CHECK IT REPLACES DEMONSTRABLY DID NOT WORK.
 * `pricing.sku.spec.ts` asserted that every `PHOTOGRAPHED_SIZE` value appears in
 * `FORM_META[form].sizes`, and its own comment says why: a size the catalogue
 * does not sell yields a 404, "a broken tile on a product card". But `Closure`
 * was set to `'SINGLE'`, which IS in `FORM_META.Closure.sizes` — so the
 * assertion passed, for months, on a tile that was 404ing the whole time,
 * because no `VERA-CLO-SINGLE.png` has ever existed. The check was on the wrong
 * property: being sellable is a PROXY for the plate existing, and the proxy
 * holds only while the two vocabularies agree. They do not (six forms differ;
 * the VRG-780 task file has the table).
 *
 * So the exception is not a loosening. The spec now requires an off-catalogue
 * size to be one a MODEL WAS BUILT FROM — `primaryTagModel(form) !== undefined`
 * — which is evidence that the plate is really there, since the generator reads
 * that exact file and asserts its aspect and fill ratio against the declared
 * dimensions before it will emit anything. That is a stronger guarantee than
 * membership in a list of sellable sizes, not a weaker one.
 *
 * Reconciling the two vocabularies is a separate ticket. Until it happens, this
 * map is the record of where they part company on a picture.
 */
export const PHOTOGRAPHED_SIZE_OFF_CATALOGUE: Readonly<Partial<Record<TagFormFactor, string>>> = {
  Closure:
    "the shot library speaks the SUPPLY vocabulary (`S68`/`S102`/`D110`/`D178`); "
    + "`FORM_META` carries `SINGLE`/`DOUBLE`. `S68` is a single-arm Closure, so it is a "
    + "true picture of what SINGLE ships — at a length the shop cannot name.",
  FabricLabel:
    "the flat labels are shot at `65x25mm` and `70x25mm`; `FORM_META` carries "
    + "`45x25mm`/`70x20mm`. No plate exists at either catalogue token, so there is no "
    + "on-catalogue choice to prefer.",
}

/**
 * The primary image for a FORM, or `undefined` when nothing has been shot.
 *
 * ⚠️ RETURNS `undefined` RATHER THAN A PLACEHOLDER. Choosing the fallback is the
 * CALLER's job, because the right fallback differs by surface — the dev mock
 * wants something visible, the shop wants Shopify's own flat, the bake step
 * wants to skip the combination entirely. Baking one in here would force all
 * three to the same wrong answer.
 */
export const primaryTagImage = (form: TagFormFactor): string | undefined => {
  const size = PHOTOGRAPHED_SIZE[form]
  return size === undefined ? undefined : tagLayerAsset({form, size})
}

/**
 * The full pair for one variant. Callers composite these, or hand them to the bake step.
 *
 * Returns names unconditionally — it cannot know which files a photographer has actually
 * delivered. Existence is the RENDERER's problem: the shop UI falls back to the flat
 * Shopify image when a layer 404s, so a half-shot library degrades to today's behaviour
 * per-variant instead of breaking the page.
 */
export const resolveVariantLayers = (
  key: TagAppearanceKey,
  quantity: number,
): VariantLayers => ({
  packageSrc: packageLayerAsset(quantity),
  tagSrc: tagLayerAsset(key),
})

/* ═══════════════════════════════════════════════════════════════════════════
   VRG-780 — THE SAME APPEARANCE, AS A ROTATABLE MODEL.

   A tag's thumbnail stays a still; hovering it spins the real part. That only
   works if the model and the still are THE SAME OBJECT, so the model resolves
   off `TagAppearanceKey` — the identical key `tagLayerAsset` resolves off, and
   `tagModelName` is literally `tagLayerName`. It is not a second naming scheme
   that happens to agree today: a model whose name were derived separately
   would drift, and the failure is silent and specific — a WHITE colourway
   spinning a black tag, which reads as a product photograph of the wrong
   product.

   The generator (`context/scripts/product-models/build-tag-glbs.mjs`) emits
   `out/tags/<stem>.glb` under exactly that stem, which is what makes the two
   halves meet. `lib/parts.mjs` names this resolver as the app-side half of
   that contract.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ WHICH APPEARANCES A GLB ACTUALLY EXISTS FOR — a property of the MODEL
 * LIBRARY, not of the catalogue, exactly as `PHOTOGRAPHED_SIZE` is a property
 * of the shot library. The same reasoning applies and is not repeated: the
 * catalogue offers sizes nobody has built a model of, and a form stops being
 * "modelled" the day a new GLB lands, with no catalogue change.
 *
 * It is a hand-written list rather than a directory read because this is a
 * browser module and there is no directory to read. The authoritative list is
 * what the generator prints; `pnpm --filter platform test:guards` asserts every
 * entry here is a name `tagModelName` can produce and that it parses back to a
 * real (form, size), which is the half of the contract CI can check. That the
 * list matches the files on disk is a developer-machine check — run the
 * generator and diff. The honest limit is stated rather than implied: a green
 * CI run proves the names are well formed, not that the bytes exist.
 *
 * ── Why the Card's size segment reads `856x54` ────────────────────────────
 * `sizeCode` strips `mm` and every NON-ALPHANUMERIC character. `x` is
 * alphanumeric and `.` is not, so ID-1's `85.6x54mm` comes out `856x54`: the
 * decimal point goes, the separator stays. The supply plate
 * `VERA-CRD-856x54.png` is named to match, exactly as every other plate is.
 *
 * ⚠️ An earlier draft of this file claimed the opposite — that the contract
 * produced `85654` and the PLATE was misnamed — and listed the card under that
 * invented stem, with a rename step in the staging script to "correct" the
 * plate on its way in. The effect was that `primaryTagModel('Card')` returned
 * `undefined`: the resolver computed the real name, the list held the invented
 * one, and the Card tile silently lost its model while every other tile kept
 * theirs. Nothing failed, because every test compared the invented name
 * against itself. It was caught by driving the shop in a browser and reading
 * the request the page actually made, which is why the staging script now
 * compares its own staged set against this list rather than trusting it.
 */
export const MODELLED_APPEARANCES: ReadonlySet<string> = new Set([
  // Sticker — all five orderable sizes. Modelled as DELIVERED: liner on, flat,
  // unpeeled. The `-BACK` plates are peel shots and are deliberately not used as
  // back textures (a lit 2D curl painted on a flat face breaks the moment the
  // model turns, which destroys the only advantage a GLB has over a hover GIF).
  'VERA-STK-5x5', 'VERA-STK-10', 'VERA-STK-20x20', 'VERA-STK-28', 'VERA-STK-45x25',
  // Solid — 6mm and 12mm read `draft` in the price list, so they are not built.
  'VERA-SLD-22x22', 'VERA-SLD-30',
  // Fabric Patch — 30mm is modelled ROUND (the price list and the plate agree;
  // the catalogue's `30x30mm` token is the outlier). 16x16mm is NOT built: its
  // only candidate render is the 30mm patch's, and is round where the part is
  // square. XD and EMBR have plates but read UNAVAILABLE in the price list.
  'VERA-FPT-25x25', 'VERA-FPT-25x25-WHITE',
  'VERA-FPT-30', 'VERA-FPT-30-WHITE',
  'VERA-FPT-45x25', 'VERA-FPT-45x25-WHITE',
  // Fabric Label (FLAT). The two LOOP sizes are deferred — a folded two-panel
  // part is not a slab, and flattening it into a rectangle would misrepresent it.
  'VERA-FLB-65x25', 'VERA-FLB-65x25-WHITE',
  'VERA-FLB-70x25', 'VERA-FLB-70x25-WHITE',
  // Closure — `S` is a round head at one END with one arm, `D` a head CENTRED
  // with arms both ways. ⚠️ These four size tokens are the SUPPLY vocabulary;
  // `FORM_META.Closure.sizes` carries `SINGLE`/`DOUBLE`. See PHOTOGRAPHED_SIZE.
  'VERA-CLO-S68', 'VERA-CLO-S102', 'VERA-CLO-D110', 'VERA-CLO-D178',
  // Dial — one-sided by construction (ferrite-bonded, sits against the movement),
  // so there is no back artwork to miss. The slot and the pin hole are real holes
  // in the mesh rather than dark patches painted on a disc.
  'VERA-DIAL-28',
  // Card — VRG-749's model, kept on its ANALYTIC path: ISO/IEC 7810 states a
  // card's corner radius and thickness, which is a better source for a standard
  // part than a trace of a render of one.
  'VERA-CRD-856x54',
])

/**
 * Built, but NOT reachable from a `TagAppearanceKey` — declared rather than
 * omitted, so the absence is a decision with a reason attached instead of a
 * gap nobody can tell from an oversight.
 *
 * `Paper` is PROPOSED (tag-shop-catalog.md §9) and is not in the
 * `TagFormFactor` union, so `tagModelName` cannot name these two. The models
 * are built anyway — the plates exist and A4 / US Letter are standards, so
 * nothing about them is provisional — and they cost two files and are ready on
 * the day the form lands. Moving them into `MODELLED_APPEARANCES` is then the
 * whole change on this side.
 */
export const MODELLED_NOT_IN_CATALOGUE: Readonly<Record<string, string>> = {
  'VERA-PPR-A4':
    '`Paper` is a proposed form and is absent from the TagFormFactor union, so no '
    + 'appearance key can name it. Built and waiting.',
  'VERA-PPR-LTR':
    'Same as VERA-PPR-A4: `Paper` is a proposed form with no entry in the TagFormFactor '
    + 'union, so no appearance key can name it. US Letter, 215.9x279.4mm, built and waiting.',
}

/**
 * The bare model filename for an appearance, without root or extension.
 *
 * Deliberately a one-line delegation to `tagLayerName` rather than its own
 * join: one function decides what an appearance is CALLED, so the still and the
 * model cannot come out named differently. Views and sequence suffixes are not
 * passed because a model has neither — it IS every view.
 */
export const tagModelName = (key: TagAppearanceKey): string => tagLayerName(key)

/**
 * The GLB for an appearance, or `undefined` when none has been built.
 *
 * ⚠️ RETURNS `undefined` RATHER THAN A URL THAT WILL 404, which is the opposite
 * of `tagLayerAsset`'s contract and deliberately so. A missing still degrades to
 * a placeholder in a box that is already there; a missing MODEL would mount a
 * viewer, fetch `@google/model-viewer`, fail, and then report the failure in
 * words ("The 3D view could not be loaded on this device") — a promise made and
 * broken, on a surface where nothing was promised. The caller needs to know
 * BEFORE it renders an affordance, so existence is answered here.
 */
export const tagModelAsset = (key: TagAppearanceKey): string | undefined => {
  const name = tagModelName(key)
  return MODELLED_APPEARANCES.has(name) ? `${TAG_IMAGERY_ROOT}/model/${name}.glb` : undefined
}

/**
 * The model for a FORM — the hover partner of `primaryTagImage`.
 *
 * ⚠️ IT RESOLVES THROUGH `PHOTOGRAPHED_SIZE`, THE SAME MAP, AND THAT IS THE
 * POINT. The shop grid is product-level: one tile per form, one still, so the
 * only appearance it can name is the form's representative one. Reading a
 * separate "representative model size" map here would let the two diverge — the
 * tile would show a 25x25mm patch and spin a 45x25mm one, with nothing to catch
 * it, because both files exist and both resolve. Sharing the map makes the
 * divergence unrepresentable.
 *
 * The cost is that a form with no photographed size gets no model even when one
 * is built, and that is the right trade: when the still is a placeholder there
 * is nothing for the model to be the same object as, and a hover that replaces
 * a stand-in photograph with a different-looking real part is the same lie in a
 * quieter voice.
 */
export const primaryTagModel = (form: TagFormFactor): string | undefined => {
  const size = PHOTOGRAPHED_SIZE[form]
  return size === undefined ? undefined : tagModelAsset({form, size})
}
